// SYSTEM AUDITOR — Standalone admin page (cross-tool)
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../utils/api';
import { fmtDateTime, fmtDate } from '../../utils/dateFmt';

const KIND_META = {
  kyc:       { icon: '🆔', color: 'amber',   tool: 'CityPro' },
  order:     { icon: '📦', color: 'teal',    tool: 'CityPro' },
  request:   { icon: '📩', color: 'fuchsia', tool: 'CityPro' },
  vyapar:    { icon: '🧾', color: 'orange',  tool: 'Vyapar' },
  health:    { icon: '🩺', color: 'red',     tool: 'Health' },
  fieldpro:  { icon: '📊', color: 'violet',  tool: 'FieldPro' },
  withdraw:  { icon: '💸', color: 'rose',    tool: 'AT Wallet' },
  deposit:   { icon: '💰', color: 'green',   tool: 'AT Wallet' },
  boost:     { icon: '🚀', color: 'orange',  tool: 'CityPro' },
};

export default function SystemAuditor() {
  const [data, setData] = useState({ tasks: [], counts: {} });
  const [kinds, setKinds] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [q, setQ] = useState('');
  const [voiceListening, setVoiceListening] = useState(false);
  const [voiceHeard, setVoiceHeard] = useState('');
  const [autoRead, setAutoRead] = useState(() => localStorage.getItem('sa_auto_read') === '1');
  const [playState, setPlayState] = useState('idle'); // idle | playing | paused
  const [playIdx, setPlayIdx] = useState(0);
  const [handsFreeHeard, setHandsFreeHeard] = useState('');
  const [pendingConfirm, setPendingConfirm] = useState(null); // {task, action} awaiting voice confirmation
  const pendingConfirmRef = useRef(null);
  const playQueueRef = useRef([]);
  const playIdxRef = useRef(0);  // Latest playIdx for closures inside SR handlers
  const bulkVoiceRef = useRef(null);
  const recRef = useRef(null);
  const seenRef = useRef(new Set());       // Set<`${kind}:${id}`> — tasks we've already announced
  const primedRef = useRef(false);         // First load primes without speaking
  const autoReadRef = useRef(autoRead);    // Latest autoRead for interval closure
  useEffect(() => { autoReadRef.current = autoRead; localStorage.setItem('sa_auto_read', autoRead ? '1' : '0'); }, [autoRead]);

  const speakTask = useCallback((t, i, prefix = '') => {
    if (!('speechSynthesis' in window)) return;
    const kindLabel = { kyc: 'KYC', order: 'ऑर्डर', request: 'अनुरोध', vyapar: 'व्यापार बिल', health: 'हेल्थ', fieldpro: 'फील्ड प्रो', withdraw: 'AT निकासी', deposit: 'AT जमा', boost: 'बूस्ट' }[t.kind] || t.kind;
    const when = t.at ? fmtDateTime(t.at) : '';
    const msg = `${prefix}${prefix ? ' ' : ''}${i != null ? `Task ${i + 1}. ` : ''}${kindLabel}. ${t.title}. ${when ? 'Time ' + when + '.' : ''}`;
    const u = new SpeechSynthesisUtterance(msg);
    u.lang = 'hi-IN'; u.rate = 0.95;
    const voices = window.speechSynthesis.getVoices?.() || [];
    const hi = voices.find((v) => v.lang?.startsWith('hi')) || voices.find((v) => /hindi|india/i.test(v.name || ''));
    if (hi) u.voice = hi;
    window.speechSynthesis.speak(u);
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/system-auditor/scan', { params: kinds ? { kinds } : {} });
      const tasks = r.data?.tasks || [];
      // Diff against seenRef to find genuinely new tasks
      const currentIds = new Set(tasks.map((t) => `${t.kind}:${t.id}`));
      if (primedRef.current && autoReadRef.current) {
        const newOnes = tasks.filter((t) => !seenRef.current.has(`${t.kind}:${t.id}`));
        if (newOnes.length > 0) {
          // Speak up to 3 fresh ones sequentially; announce count first
          try {
            const intro = new SpeechSynthesisUtterance(`${newOnes.length} नए request आए हैं.`);
            intro.lang = 'hi-IN'; intro.rate = 0.95;
            const voices = window.speechSynthesis.getVoices?.() || [];
            const hi = voices.find((v) => v.lang?.startsWith('hi'));
            if (hi) intro.voice = hi;
            window.speechSynthesis.cancel();
            window.speechSynthesis.speak(intro);
          } catch { /* noop */ }
          newOnes.slice(0, 3).forEach((t, i) => speakTask(t, i, 'नया।'));
          toast.success(`🔊 ${newOnes.length} new request auto-announced`);
        }
      }
      seenRef.current = currentIds;
      primedRef.current = true;
      setData(r.data);
      setSelected(new Set());
    }
    catch { toast.error('Scan failed'); }
  }, [kinds, speakTask]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  // ===== Live SSE stream — <1s latency for new incoming requests =====
  const [sseLive, setSseLive] = useState(false);
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/system-auditor/stream?token=${encodeURIComponent(token)}${kinds ? `&kinds=${kinds}` : ''}`;
    let es;
    try {
      es = new EventSource(url);
    } catch { return; }
    es.onopen = () => setSseLive(true);
    es.onerror = () => setSseLive(false);
    es.addEventListener('new', (e) => {
      try {
        const payload = JSON.parse(e.data);
        const tasks = payload.tasks || [];
        if (tasks.length === 0) return;
        // Announce via TTS if autoRead is ON (respect same toggle)
        if (autoReadRef.current && 'speechSynthesis' in window) {
          const intro = new SpeechSynthesisUtterance(`${tasks.length} नए request आए हैं.`);
          intro.lang = 'hi-IN'; intro.rate = 0.95;
          const voices = window.speechSynthesis.getVoices?.() || [];
          const hi = voices.find((v) => v.lang?.startsWith('hi'));
          if (hi) intro.voice = hi;
          window.speechSynthesis.cancel();
          window.speechSynthesis.speak(intro);
          tasks.slice(0, 3).forEach((t, i) => speakTask(t, i, 'नया।'));
        }
        toast.success(`🔴 LIVE: ${tasks.length} new request${tasks.length > 1 ? 's' : ''}`);
        // Refresh full snapshot to render new rows
        load();
      } catch { /* noop */ }
    });
    return () => { try { es.close(); } catch { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kinds]);

  const filtered = data.tasks.filter((t) => !q || t.title.toLowerCase().includes(q.toLowerCase()));
  const toggle = (t) => { const s = new Set(selected); const k = `${t.kind}:${t.id}`; s.has(k) ? s.delete(k) : s.add(k); setSelected(s); };
  const isSel = (t) => selected.has(`${t.kind}:${t.id}`);
  const selAll = () => setSelected(new Set(filtered.map((t) => `${t.kind}:${t.id}`)));
  const clearSel = () => setSelected(new Set());

  const doAction = async (t, action) => {
    try { await api.post('/admin/system-auditor/action', { kind: t.kind, id: t.id, action }); toast.success(`✓ ${action}d`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const bulk = async (action) => {
    if (selected.size === 0) { toast.error('Select tasks first'); return; }
    if (!window.confirm(`${action.toUpperCase()} ${selected.size} tasks?`)) return;
    const items = Array.from(selected).map((k) => {
      const [kind, id] = k.split(':');
      return { kind, id, action };
    });
    try {
      const r = await api.post('/admin/system-auditor/bulk', { items });
      toast.success(`✓ ${r.data.results.filter((x) => x.ok).length}/${r.data.count} succeeded`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const exportCsv = () => {
    const rows = [['#', 'kind', 'tool', 'id', 'title', 'at']];
    filtered.forEach((t, i) => rows.push([i + 1, t.kind, t.tool, t.id, `"${(t.title || '').replace(/"/g,'""')}"`, t.at]));
    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `auditor-${Date.now()}.csv`; a.click();
  };

  // Voice
  const parseVoiceCmd = (text) => {
    const t = text.toLowerCase().trim();
    let action = null;
    if (/\b(approve|approv|accept|manzoor|swikaar|स्वीकार|मंजूर)\b/i.test(t)) action = 'approve';
    else if (/\b(reject|cancel|khaarij|khariz|खारिज|अस्वीकार)\b/i.test(t)) action = 'reject';
    else if (/\b(hold|rok|wait|pause|रोक|होल्ड)\b/i.test(t)) action = 'hold';
    if (!action) return null;
    const map = { one:1,first:1,ek:1,pehla:1,एक:1,पहला:1, two:2,second:2,do:2,dusra:2,दो:2, three:3,third:3,teen:3,तीन:3, four:4,char:4,चार:4, five:5,panch:5,paanch:5, six:6,chhah:6, seven:7,saat:7, eight:8,aath:8, nine:9,nau:9, ten:10,das:10 };
    const digitMatch = t.match(/\btask\s*(\d+)|\b(\d+)\s*(?:number|no|task)?\b|\b(\d+)\b/);
    let num = digitMatch ? parseInt(digitMatch[1] || digitMatch[2] || digitMatch[3]) : null;
    if (!num) for (const [k, v] of Object.entries(map)) if (new RegExp(`\\b${k}\\b`, 'i').test(t)) { num = v; break; }
    return { action, num, isAll: /\ball|sab|सब|सारे|everything/i.test(t) };
  };
  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error('Voice not supported'); return; }
    if (voiceListening) { try { recRef.current?.stop(); } catch { /* noop */ } return; }
    const rec = new SR(); rec.lang = 'hi-IN'; rec.continuous = true; rec.interimResults = false;
    rec.onstart = () => { setVoiceListening(true); toast.success('🎤 Say: "approve task 1" · "reject all" · "hold two"'); };
    rec.onresult = async (e) => {
      const last = e.results[e.results.length - 1]; if (!last?.isFinal) return;
      const heard = last[0].transcript; setVoiceHeard(heard);
      const cmd = parseVoiceCmd(heard); if (!cmd) { toast.info(`🎤 "${heard}" — samajh nahi aaya`); return; }
      if (cmd.isAll) { await bulk(cmd.action); return; }
      if (!cmd.num || cmd.num > filtered.length) { toast.error('Invalid task number'); return; }
      await doAction(filtered[cmd.num - 1], cmd.action);
      const u = new SpeechSynthesisUtterance(`Task ${cmd.num} ${cmd.action}d`); u.lang = 'en-IN'; window.speechSynthesis.speak(u);
    };
    rec.onerror = () => setVoiceListening(false);
    rec.onend = () => setVoiceListening(false);
    recRef.current = rec; rec.start();
  };
  const readAloud = () => {
    if (!('speechSynthesis' in window)) return;
    const text = filtered.length === 0 ? 'सभी काम पूरे हैं। कोई pending task नहीं है।' : `${filtered.length} pending tasks. ${filtered.slice(0, 5).map((t, i) => `Task ${i+1}: ${t.title}`).join('. ')}`;
    const u = new SpeechSynthesisUtterance(text); u.lang = 'hi-IN'; u.rate = 0.95;
    const voices = window.speechSynthesis.getVoices?.() || [];
    const hi = voices.find((v) => v.lang?.startsWith('hi')) || voices.find((v) => /hindi|india/i.test(v.name || ''));
    if (hi) u.voice = hi;
    window.speechSynthesis.cancel(); window.speechSynthesis.speak(u);
  };

  // ===== BULK LISTEN — queue-based playback with pause/resume/skip =====
  const playNextFromQueue = useCallback(() => {
    if (!('speechSynthesis' in window)) return;
    const queue = playQueueRef.current;
    setPlayIdx((i) => {
      const next = i + 1;
      playIdxRef.current = next;
      if (next >= queue.length) {
        setPlayState('idle');
        try { bulkVoiceRef.current?.stop(); } catch { /* noop */ }
        bulkVoiceRef.current = null;
        toast.success('✓ Sabhi requests sun liye');
        return 0;
      }
      const t = queue[next];
      const u = new SpeechSynthesisUtterance(`Task ${next + 1}. ${t.title}. Approve, reject, ya skip bolo.`);
      u.lang = 'hi-IN'; u.rate = 0.95;
      const voices = window.speechSynthesis.getVoices?.() || [];
      const hi = voices.find((v) => v.lang?.startsWith('hi'));
      if (hi) u.voice = hi;
      u.onend = () => { if (playState !== 'paused') playNextFromQueue(); };
      window.speechSynthesis.speak(u);
      return next;
    });
  }, [playState]);

  const startBulkListen = () => {
    if (!('speechSynthesis' in window)) return toast.error('TTS not supported');
    if (filtered.length === 0) return toast.error('Koi pending task nahi');
    window.speechSynthesis.cancel();
    playQueueRef.current = [...filtered];
    playIdxRef.current = -1;
    setPlayIdx(-1);
    setPlayState('playing');
    // Prime with intro
    const intro = new SpeechSynthesisUtterance(`${filtered.length} pending requests padhe ja rahe hain. Hands-free voice ON — bolo approve, reject, ya skip.`);
    intro.lang = 'hi-IN'; intro.rate = 0.95;
    const voices = window.speechSynthesis.getVoices?.() || [];
    const hi = voices.find((v) => v.lang?.startsWith('hi'));
    if (hi) intro.voice = hi;
    intro.onend = () => playNextFromQueue();
    window.speechSynthesis.speak(intro);
    // Start hands-free listener — approve / reject / skip / stop
    startBulkVoiceListener();
  };
  const pauseBulk = () => { window.speechSynthesis.pause(); setPlayState('paused'); };
  const resumeBulk = () => { window.speechSynthesis.resume(); setPlayState('playing'); };
  const skipBulk = () => { window.speechSynthesis.cancel(); playNextFromQueue(); setPlayState('playing'); };
  const stopBulk = () => {
    window.speechSynthesis.cancel();
    setPlayState('idle'); setPlayIdx(0); playIdxRef.current = 0;
    try { bulkVoiceRef.current?.stop(); } catch { /* noop */ }
    bulkVoiceRef.current = null;
    setHandsFreeHeard('');
    toast.success('Rok diya');
  };

  // Hands-free voice: while bulk-listening, admin can say "approve" / "reject" / "skip" / "stop"
  // → auto-executes on the CURRENT task being read. Zero-touch admin flow.
  const startBulkVoiceListener = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;  // silent — bulk playback still works
    try {
      const rec = new SR();
      rec.lang = 'hi-IN'; rec.continuous = true; rec.interimResults = false;
      rec.onresult = async (e) => {
        const last = e.results[e.results.length - 1];
        if (!last?.isFinal) return;
        const heard = (last[0].transcript || '').trim();
        setHandsFreeHeard(heard);
        const lc = heard.toLowerCase();

        // === STAGE 2: Awaiting confirmation of a previously-heard action ===
        if (pendingConfirmRef.current) {
          const { task, action } = pendingConfirmRef.current;
          const isConfirm = /\b(confirm|haan|yes|sahi|bilkul|ok|okay|karo|do|कर\s*दो|हाँ)\b/i.test(lc);
          const isCancel  = /\b(cancel|nahi|no|abort|ruk|band|नहीं|रद्द)\b/i.test(lc);
          if (isConfirm) {
            pendingConfirmRef.current = null; setPendingConfirm(null);
            try { await doAction(task, action); } catch { /* noop */ }
            const done = new SpeechSynthesisUtterance(`${action}ed. Aage badh rahe hain.`);
            done.lang = 'hi-IN'; done.rate = 0.95;
            const voices2 = window.speechSynthesis.getVoices?.() || [];
            const hi2 = voices2.find((v) => v.lang?.startsWith('hi'));
            if (hi2) done.voice = hi2;
            window.speechSynthesis.cancel();
            done.onend = () => playNextFromQueue();
            window.speechSynthesis.speak(done);
            return;
          }
          if (isCancel) {
            pendingConfirmRef.current = null; setPendingConfirm(null);
            const cancel = new SpeechSynthesisUtterance('Cancel kar diya. Agla task.');
            cancel.lang = 'hi-IN'; cancel.rate = 0.95;
            window.speechSynthesis.cancel();
            cancel.onend = () => playNextFromQueue();
            window.speechSynthesis.speak(cancel);
            return;
          }
          // Unknown reply while confirming — silently ignore & keep listening
          return;
        }

        // === STAGE 1: Detect the action, then ARM confirmation before executing ===
        const isApprove = /\b(approve|approv|accept|manzoor|swikaar|स्वीकार|मंजूर)\b/i.test(lc);
        const isReject  = /\b(reject|khaarij|khariz|खारिज|अस्वीकार)\b/i.test(lc);
        const isHold    = /\b(hold|rok|wait|रोक|होल्ड)\b/i.test(lc);
        const isSkip    = /\b(skip|next|aage|अगला)\b/i.test(lc);
        const isStop    = /\b(stop|khatam|रोक\s*do|बंद)\b/i.test(lc);
        if (isStop) { stopBulk(); return; }
        if (isSkip) { skipBulk(); return; }
        const action = isApprove ? 'approve' : isReject ? 'reject' : isHold ? 'hold' : null;
        if (!action) return;
        const idx = playIdxRef.current;
        const task = playQueueRef.current[idx];
        if (!task) return;
        // ARM confirmation — read back the intended action + wait for "confirm" or "cancel"
        pendingConfirmRef.current = { task, action };
        setPendingConfirm({ task, action });
        const actionHi = { approve: 'approve', reject: 'reject', hold: 'hold' }[action];
        const q = new SpeechSynthesisUtterance(`${actionHi} karna hai? Haan bolo confirm karo, ya cancel bolo.`);
        q.lang = 'hi-IN'; q.rate = 0.95;
        const voices = window.speechSynthesis.getVoices?.() || [];
        const hi = voices.find((v) => v.lang?.startsWith('hi'));
        if (hi) q.voice = hi;
        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(q);
      };
      rec.onerror = () => { /* auto-restart via onend */ };
      rec.onend = () => {
        // Keep listening as long as bulk playback is active
        if (playState !== 'idle' && bulkVoiceRef.current === rec) {
          try { rec.start(); } catch { /* browser may throttle */ }
        }
      };
      bulkVoiceRef.current = rec;
      rec.start();
    } catch { /* noop */ }
  };

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-red-950 via-slate-950 to-cyan-950 text-white" data-testid="system-auditor">
      <div className="max-w-5xl mx-auto px-3 pb-16">
        <div className="flex items-center gap-2 mb-3">
          <Link to="/admin/new-tools" className="text-xs text-slate-400 hover:text-white">← Admin</Link>
        </div>
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight">🛰️ System Auditor</h1>
        <p className="text-[11px] opacity-70 mb-3">Cross-tool pending task console · voice commands (Hinglish) · bulk actions · CSV export</p>

        {/* LLM Credits Monitor */}
        <LLMMonitorPanel />

        {/* Kind counts */}
        <div className="grid grid-cols-4 sm:grid-cols-9 gap-1 mb-3">
          {Object.entries(KIND_META).map(([k, m]) => (
            <button key={k} onClick={() => setKinds(kinds === k ? '' : k)}
                    data-testid={`sa-filter-${k}`}
                    className={`rounded-lg p-1.5 text-center text-[9px] font-black border ${kinds === k ? `border-${m.color}-400 bg-${m.color}-500/30` : 'border-slate-700 bg-slate-800/60'}`}>
              <div className="text-lg">{m.icon}</div>
              <div className="uppercase opacity-80">{k}</div>
              <div className="text-sm">{data.counts?.[k] ?? 0}</div>
            </button>
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <input placeholder="🔎 Search title" value={q} onChange={(e) => setQ(e.target.value)} data-testid="sa-search"
                 className="flex-1 min-w-[180px] px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <button onClick={load} className="px-2 py-1 rounded bg-white/10 text-xs">↻ Rescan</button>
          <span data-testid="sa-sse-status" className={`px-2 py-1 rounded text-[10px] font-black ${sseLive ? 'bg-emerald-500 text-black animate-pulse' : 'bg-slate-700 text-slate-300'}`}>{sseLive ? '🔴 LIVE' : '💤 Poll'}</span>
          <button onClick={readAloud} data-testid="sa-read" className="px-2 py-1 rounded bg-rose-500 text-white text-xs font-black">🔊 Read</button>
          {/* Bulk Listen queue controls */}
          {playState === 'idle' && (
            <button onClick={startBulkListen} data-testid="sa-bulk-listen-start" className="px-2 py-1 rounded bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-xs font-black">▶️ Play All ({filtered.length})</button>
          )}
          {playState === 'playing' && (
            <>
              <button onClick={pauseBulk} data-testid="sa-bulk-listen-pause" className="px-2 py-1 rounded bg-amber-500 text-black text-xs font-black">⏸ Pause</button>
              <button onClick={skipBulk} data-testid="sa-bulk-listen-skip" className="px-2 py-1 rounded bg-cyan-500 text-black text-xs font-black">⏭ Skip</button>
              <button onClick={stopBulk} className="px-2 py-1 rounded bg-red-500 text-white text-xs font-black">⏹ Stop</button>
              <span className="text-[10px] text-purple-200">{playIdx + 1}/{playQueueRef.current.length}</span>
              <span className="text-[9px] text-emerald-300 font-black animate-pulse">🎤 hands-free</span>
              {pendingConfirm && (
                <span className="text-[10px] font-black px-2 py-0.5 rounded bg-amber-500 text-black animate-pulse">
                  Confirm {pendingConfirm.action}? Say "haan" / "nahi"
                </span>
              )}
              {handsFreeHeard && <span className="text-[9px] text-slate-300 italic">"{handsFreeHeard.slice(0, 40)}"</span>}
            </>
          )}
          {playState === 'paused' && (
            <>
              <button onClick={resumeBulk} data-testid="sa-bulk-listen-resume" className="px-2 py-1 rounded bg-emerald-500 text-black text-xs font-black">▶️ Resume</button>
              <button onClick={skipBulk} className="px-2 py-1 rounded bg-cyan-500 text-black text-xs font-black">⏭ Skip</button>
              <button onClick={stopBulk} className="px-2 py-1 rounded bg-red-500 text-white text-xs font-black">⏹ Stop</button>
              <span className="text-[10px] text-amber-200">Paused {playIdx + 1}/{playQueueRef.current.length}</span>
            </>
          )}
          <button
            onClick={() => setAutoRead((v) => !v)}
            data-testid="sa-auto-read-toggle"
            title="Auto-speak new incoming requests every 30s"
            className={`px-2 py-1 rounded text-xs font-black ${autoRead ? 'bg-emerald-500 text-black animate-pulse' : 'bg-slate-700 text-slate-200'}`}
          >
            {autoRead ? '🔴 Auto-Read ON' : '💤 Auto-Read'}
          </button>
          <button onClick={startVoice} data-testid="sa-voice"
                  className={`px-2 py-1 rounded font-black text-xs ${voiceListening ? 'bg-red-500 text-white animate-pulse' : 'bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white'}`}>
            {voiceListening ? '⏹ Stop' : '🎤 Voice'}
          </button>
          <button onClick={exportCsv} data-testid="sa-csv" className="px-2 py-1 rounded bg-slate-700 text-white text-xs font-black">⬇️ CSV</button>
        </div>
        {voiceListening && voiceHeard && <div className="text-[10px] text-fuchsia-200 mb-2">Heard: <b>{voiceHeard}</b></div>}

        {/* Bulk toolbar */}
        {selected.size > 0 && (
          <div className="rounded-xl border border-cyan-400/40 bg-cyan-500/10 p-2 mb-2 flex flex-wrap items-center gap-2" data-testid="sa-bulk-bar">
            <div className="text-xs font-black text-cyan-200">{selected.size} selected</div>
            <button onClick={() => bulk('approve')} data-testid="sa-bulk-approve" className="px-2 py-1 rounded bg-emerald-500 text-black text-[11px] font-black">✓ Approve All</button>
            <button onClick={() => bulk('hold')} className="px-2 py-1 rounded bg-amber-500 text-black text-[11px] font-black">⏸ Hold</button>
            <button onClick={() => bulk('reject')} data-testid="sa-bulk-reject" className="px-2 py-1 rounded bg-red-500 text-white text-[11px] font-black">✕ Reject</button>
            <button onClick={clearSel} className="px-2 py-1 rounded bg-slate-700 text-slate-200 text-[11px]">Clear</button>
          </div>
        )}

        <div className="flex items-center gap-2 mb-2">
          <button onClick={selAll} className="text-[10px] px-2 py-1 rounded bg-white/10">☑ Select all ({filtered.length})</button>
          {filtered.length !== data.tasks.length && <span className="text-[10px] opacity-70">filtered from {data.tasks.length}</span>}
        </div>

        {/* Task rows */}
        <div className="space-y-1">
          {filtered.map((t, i) => {
            const m = KIND_META[t.kind] || { icon: '❔', tool: '?' };
            return (
              <div key={`${t.kind}-${t.id}`} className={`rounded-xl border ${isSel(t) ? 'border-cyan-400 bg-cyan-500/10' : 'border-slate-700 bg-slate-900/60'} p-2 flex items-start gap-2 text-xs`} data-testid={`sa-task-${t.kind}-${t.id}`}>
                <input type="checkbox" checked={isSel(t)} onChange={() => toggle(t)} data-testid={`sa-check-${t.kind}-${t.id}`} />
                <div className="w-6 text-center opacity-70 font-black">{i + 1}</div>
                <div className="text-xl">{m.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-black truncate">{t.title}</div>
                  <div className="text-[9px] opacity-60">{m.tool} · {t.kind} · {fmtDateTime(t.at)}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => speakTask(t, i)} data-testid={`sa-listen-${t.kind}-${t.id}`} title="Listen (Hindi)" className="px-2 py-0.5 rounded bg-cyan-500 text-black text-[10px] font-black">🔊</button>
                  {t.actions?.includes('approve') && <button onClick={() => doAction(t, 'approve')} data-testid={`sa-approve-${t.id}`} className="px-2 py-0.5 rounded bg-emerald-500 text-black text-[10px] font-black">✓</button>}
                  {t.actions?.includes('hold') && <button onClick={() => doAction(t, 'hold')} data-testid={`sa-hold-${t.id}`} className="px-2 py-0.5 rounded bg-amber-500 text-black text-[10px] font-black">⏸</button>}
                  {t.actions?.includes('reject') && <button onClick={() => doAction(t, 'reject')} data-testid={`sa-reject-${t.id}`} className="px-2 py-0.5 rounded bg-red-500 text-white text-[10px] font-black">✕</button>}
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && <div className="text-center opacity-60 py-10">✅ All clear · no pending tasks</div>}
        </div>
      </div>
    </div>
  );
}

// ============ LLM CREDITS MONITOR PANEL ============
function LLMMonitorPanel() {
  const [open, setOpen] = useState(false);
  const [usage, setUsage] = useState(null);
  const [toggles, setToggles] = useState(null);
  const [days, setDays] = useState(7);
  const [savingKey, setSavingKey] = useState(null);
  const [liveSnap, setLiveSnap] = useState(null);   // {totals, per_service, tail} from SSE
  const [liveTicks, setLiveTicks] = useState(0);
  const flashRef = useRef(null);
  const esRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [u, t] = await Promise.all([
        api.get(`/admin/system-auditor/llm-usage?days=${days}`),
        api.get('/admin/system-auditor/llm-toggles'),
      ]);
      setUsage(u.data); setToggles(t.data);
    } catch { toast.error('LLM monitor load failed'); }
  }, [days]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // 🛰️ SSE Live Stream — auto-connect while panel is open
  useEffect(() => {
    if (!open) {
      if (esRef.current) { try { esRef.current.close(); } catch { /* noop */ } esRef.current = null; }
      return;
    }
    const token = localStorage.getItem('anant_token') || localStorage.getItem('token');
    if (!token) return;
    const base = process.env.REACT_APP_BACKEND_URL;
    const es = new EventSource(`${base}/api/admin/system-auditor/llm-stream?token=${encodeURIComponent(token)}&days=1`);
    esRef.current = es;
    es.onmessage = (e) => {
      try {
        const d = JSON.parse(e.data);
        if (d.type === 'llm_snapshot') {
          setLiveSnap(d);
          setLiveTicks((n) => n + 1);
        }
      } catch { /* noop */ }
    };
    es.addEventListener('llm_new', (e) => {
      try {
        const d = JSON.parse(e.data);
        const n = (d.calls || []).length;
        if (n > 0) {
          toast(`🧠 ${n} new AI call${n > 1 ? 's' : ''} · ${d.calls[0]?.service || ''}`, { icon: '🛰️' });
          // Flash indicator
          if (flashRef.current) {
            flashRef.current.classList.add('bg-emerald-400');
            setTimeout(() => flashRef.current?.classList.remove('bg-emerald-400'), 800);
          }
        }
      } catch { /* noop */ }
    });
    es.onerror = () => { /* auto-reconnect handled by browser */ };
    return () => { try { es.close(); } catch { /* noop */ } };
  }, [open]);

  const setState = async (key, val) => {
    setSavingKey(key);
    try {
      const r = await api.post('/admin/system-auditor/llm-toggles', { patch: { [key]: val } });
      setToggles({ ...toggles, toggles: r.data.toggles });
      toast.success(`${key} → ${val.toUpperCase()}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSavingKey(null); }
  };

  return (
    <div className="mb-3 rounded-2xl border-2 border-purple-400/40 bg-purple-950/40 overflow-hidden" data-testid="sa-llm-monitor">
      <button onClick={() => setOpen(!open)} data-testid="sa-llm-toggle-panel"
              className="w-full text-left px-3 py-2 flex items-center justify-between hover:bg-purple-900/40">
        <div className="flex items-center gap-2">
          <span className="text-lg">🧠</span>
          <div>
            <div className="text-xs font-black text-purple-200 flex items-center gap-2">
              Emergent LLM Credits Monitor
              {open && (
                <span ref={flashRef} data-testid="sa-llm-live-dot"
                      className="inline-block w-2 h-2 rounded-full bg-purple-400 animate-pulse transition-colors"
                      title={`Live tick #${liveTicks}`} />
              )}
              {open && liveSnap && <span className="text-[9px] opacity-70 font-normal">Live · {liveSnap.totals?.calls || 0} today</span>}
            </div>
            <div className="text-[9px] opacity-70">Per-service usage · ON/OFF/RESTRICT toggles · live SSE stream</div>
          </div>
        </div>
        <span className="text-purple-300">{open ? '▼' : '▶'}</span>
      </button>
      {open && (
        <div className="p-3 space-y-3">
          <div className="flex items-center gap-2">
            <select value={days} onChange={(e) => setDays(parseInt(e.target.value))}
                    data-testid="sa-llm-days"
                    className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs">
              <option value={1}>Today</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
            </select>
            <button onClick={load} className="px-2 py-1 rounded bg-slate-800 text-xs">🔄 Reload</button>
            {liveSnap && (
              <span className="text-[9px] px-2 py-1 rounded-full bg-emerald-900/40 border border-emerald-500/40 text-emerald-300">
                🛰️ Live · tick {liveTicks}
              </span>
            )}
          </div>
          {usage && (
            <div className="grid grid-cols-3 gap-2">
              <div className="p-2 rounded-lg bg-slate-900 border border-slate-800 text-center">
                <div className="text-[9px] opacity-70">Total Calls</div>
                <div className="text-lg font-black text-purple-300">{usage.totals?.calls || 0}</div>
              </div>
              <div className="p-2 rounded-lg bg-slate-900 border border-slate-800 text-center">
                <div className="text-[9px] opacity-70">Success Rate</div>
                <div className="text-lg font-black text-emerald-300">{usage.totals?.success_rate ?? 100}%</div>
              </div>
              <div className="p-2 rounded-lg bg-slate-900 border border-slate-800 text-center">
                <div className="text-[9px] opacity-70">Errors</div>
                <div className="text-lg font-black text-rose-300">{usage.totals?.errors || 0}</div>
              </div>
            </div>
          )}
          {/* Live tail — last 5 calls */}
          {liveSnap?.tail?.length > 0 && (
            <div className="p-2 rounded-lg bg-slate-900/60 border border-emerald-500/20 space-y-1" data-testid="sa-llm-live-tail">
              <div className="text-[9px] uppercase tracking-widest font-black text-emerald-300">🛰️ Live Tail (last 5)</div>
              {liveSnap.tail.map((t, i) => (
                <div key={i} className="flex items-center justify-between text-[10px]">
                  <span className="truncate">{t.service} <span className="opacity-60">· {t.model?.split('-')[0]}</span></span>
                  <span className={t.ok ? 'text-emerald-300' : 'text-rose-300'}>{t.ok ? '✓' : '✗'} {new Date(t.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                </div>
              ))}
            </div>
          )}
          {/* Per-service toggles */}
          <div className="space-y-1.5">
            <div className="text-[10px] uppercase tracking-widest font-black opacity-70">Service Controls</div>
            {toggles && Object.entries(toggles.toggles || {}).map(([key, val]) => {
              const svcUsage = usage?.per_service?.find((p) => p.service === key);
              const liveSvc = liveSnap?.per_service?.find((p) => p.service === key);
              return (
                <div key={key} className="flex items-center gap-2 p-2 rounded-lg bg-slate-900 border border-slate-800" data-testid={`sa-llm-svc-${key}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-black truncate">{key}</div>
                    {svcUsage && <div className="text-[9px] opacity-60">{svcUsage.calls} calls · {svcUsage.err} err {liveSvc && <span className="text-emerald-400">· 🛰️ {liveSvc.calls} today</span>}</div>}
                  </div>
                  <div className="flex gap-0.5">
                    {['on', 'restrict', 'off'].map((state) => (
                      <button key={state} onClick={() => setState(key, state)} disabled={savingKey === key}
                              data-testid={`sa-llm-${key}-${state}`}
                              className={`px-2 py-1 rounded text-[9px] font-black uppercase transition ${
                                val === state
                                  ? state === 'on' ? 'bg-emerald-500 text-slate-900' : state === 'restrict' ? 'bg-amber-500 text-slate-900' : 'bg-red-500 text-white'
                                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                              }`}>
                        {state}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Top users */}
          {usage?.top_users?.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[10px] uppercase tracking-widest font-black opacity-70">Top Users ({days}d)</div>
              {usage.top_users.slice(0, 10).map((u, i) => (
                <div key={i} className="flex items-center justify-between text-[10px] p-1.5 rounded bg-slate-900/60">
                  <div className="truncate">@{u.username || u.user_id.slice(0, 8)} · <span className="opacity-60">{u.service}</span></div>
                  <div className="font-black text-purple-300">{u.calls}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
